import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ordersAPI } from '../api';
import { updateUser} from './userSlice';
import { checkSavedOrderObject, compareObjects } from '../helpers';
import { CLIENT_NOTS, getClientNotify } from './notifySlice';

const hasRequiredFields = (test, requiredFields) => {
    const obj = {_id: test._id};
    requiredFields.forEach(field => {
        if (field in test) {
            obj[field] = test[field];
        }
    })
    return {hasData: (Object.keys(obj).length > 1), fields: obj}
};

const defaultOrderTitleSizes = {
    id: '50px',
    default: '100px',
}

function getOrderTitlesSize() {
    const savedTitles = JSON.parse(localStorage.getItem('titleSizes'));
    if (savedTitles) {
        return savedTitles;
    }
    return defaultOrderTitleSizes;
}

function getLastSavedOrder(settings) {
    const defaultOrder = {
        advertType: '',
        sourceType: '',
        orderType: '',
        clientName: '',
        orderComment: '',
        city: '',
        address: {
            village: '',
            street: '',
            apartmentNumber: '',
            additional: ''
        },
        date: '',
        time: '',
        clientPhone: '',
        profileType: '',
        isProfile: true,
    }
    const updateLocal = () => {localStorage.setItem('lastSavedOrder', JSON.stringify(defaultOrder))}
    
    const savedOrder = JSON.parse(localStorage.getItem('lastSavedOrder'));
    if (savedOrder === null) {
        updateLocal();
        return false;
    }
    const isValidOrder = compareObjects(savedOrder, defaultOrder);
    if (!isValidOrder) {
        updateLocal();
        return false;
    }
    const isActualObject = checkSavedOrderObject(savedOrder, settings);
    if (isActualObject) {
        return true;
    }
    updateLocal();
    return false;
}

const initialState = {
    list: [],
    error: null,
    isLoaded: false,
    months: null,
    isOrderCreating: false,
    isSavedOrderExists: false,
    orderTitles: getOrderTitlesSize(),
    sort: null,
    search: null,
    filter: null,
    deps: [],
};
                                                             //yyyy-mm
export const getOrders = createAsyncThunk('app/fetchOrders', async ({month, multi = false}, thunkAPI) => {
    let data = null;
    const {loaded: isDataLoaded, timestamp} = (thunkAPI.getState()).orders.months.find(m => m.month === month);
    thunkAPI.dispatch(setDeps([]));
    if (!isDataLoaded) {
        thunkAPI.dispatch(setLoaded(false));
        const loadedOrders = (thunkAPI.getState()).orders.list.filter(o => o.details.monthStamp === timestamp).map(o => o.id);
        data = await ordersAPI.get(month, loadedOrders);//@ts-ignore
        if (data.isOkay) {
            thunkAPI.dispatch(changeMonth({month, multi}));
            thunkAPI.dispatch(setMonthLoaded({}));

            thunkAPI.dispatch(setOrders(data.orders));

        } else {
            thunkAPI.dispatch(setError(data.message));
        }
        thunkAPI.dispatch(setLoaded(true));
    
    } else {
        thunkAPI.dispatch(changeMonth({month, multi}));
        thunkAPI.dispatch(setLoaded(true));
        return true;
    }
    return data.isOkay;
});

export const getDeps = createAsyncThunk('app/fetchOrderDeps', async (orderIds, thunkAPI) => {
    const orderList = (thunkAPI.getState()).orders.list;
    const unloadedIds = orderIds.filter(id => !orderList.some(o => o._id === id));
    if (unloadedIds.length) {
        thunkAPI.dispatch(setLoaded(false));
        const data = await ordersAPI.getDeps(unloadedIds);
        if (data.isOkay) {
            thunkAPI.dispatch(setOrders(data.orders));
        } else {
            thunkAPI.dispatch(setError(data.message));
        }
        thunkAPI.dispatch(setLoaded(true));
    
    } else {
        thunkAPI.dispatch(setLoaded(true));
    }
    thunkAPI.dispatch(setDeps(orderIds));
});

export const deleteOrder = createAsyncThunk('orders/delete', async (orderId, thunkAPI) => {
    const data = await ordersAPI.delete(orderId);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.DELETE_ORDER}));
    if (data.isOkay) {
        thunkAPI.dispatch(dropOrder(data.order._id));
    } else {
        console.log(data);
    }
});
export const recoveryOrder = createAsyncThunk('orders/recovery', async (orderId, thunkAPI) => {
    const data = await ordersAPI.delete(orderId, true);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.RECOVERY_ORDER}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateOrder(data.order));
    } else {
        console.log(data);
    }
});

export const createOrder = createAsyncThunk('orders/create', async ({requestBody, defaultState, setCreated}, thunkAPI) => {
    const data = await ordersAPI.create(requestBody);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.CREATE_ORDER}));
    if (data.isOkay) {
        setCreated(true);
        thunkAPI.dispatch(insertNewOrder(data.order));
        thunkAPI.dispatch(saveOrderLocal(defaultState));
        thunkAPI.dispatch(setNewOrderField(false));

    } else {
        setCreated(false);
    }
});

export const createOrderDep = createAsyncThunk('orders/inherit', async (orderDepData, thunkAPI) => {

    const data = await ordersAPI.createDep(orderDepData);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.CREATE_ORDER_DEP}));
    if (data.isOkay) {
        thunkAPI.dispatch(insertNewOrder(data.order));
        thunkAPI.dispatch(updateDeps(data.dependencies));
        orderDepData.closeFn();

    } else {
        console.log(data);
    }
});

export const assignOrder = createAsyncThunk('orders/assign', async ({orderId, userId}, thunkAPI) => {

    const data = await ordersAPI.assign(orderId, userId);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.ASSIGN_ORDER}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateOrder(data.order));
        thunkAPI.dispatch(updateUser(data.worker));
    } else {
        console.log(data);
    }
});
export const revokeOrder = createAsyncThunk('orders/revoke', async (orderId, thunkAPI) => {
    const data = await ordersAPI.revoke(orderId);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.REVOKE_ORDER}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateOrder(data.order));
        thunkAPI.dispatch(updateUser(data.worker));
    } else {
        console.log(data);
    }
});

export const resetOrder = createAsyncThunk('orders/reset', async (orderId, thunkAPI) => {
    const data = await ordersAPI.reset(orderId);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.RESET_ORDER}));
    if (data.isOkay) {
        thunkAPI.dispatch(updateOrder(data.order));
        data.worker && thunkAPI.dispatch(updateUser(data.worker));
    } else {
        console.log(data);
    }
});

export const editOrder = createAsyncThunk('orders/edit', async ({closeEditor, orderData, notifyWorker}, thunkAPI) => {
    const descriptionDataFields = ['advertType', 'sourceType', 'orderType', 'clientName', 'orderComment', 'address', 'city', 'date', 'clientPhone', 'profileType'];
    const isProfileFields = ['isProfile'];
    const workerFields = ['worker'];
    const completionDataFields = ['totalAmount', 'partsCost', 'completionDate', 'workerComment'];

    let errorData = null;
    let errorAction = null;

    try {
        const {hasData: hasDescData, fields: descData} = hasRequiredFields(orderData, descriptionDataFields);
        if (hasDescData) {
            const descriptionData = await ordersAPI.edit({orderData: descData, notifyWorker, changesType: 'description'});
            const action = CLIENT_NOTS.EDIT_ORDER_DESCRIPTION;

            if (!descriptionData.isOkay) {
                errorData = descriptionData;
                errorAction = action;
                throw new Error('edit description error');
            }
            thunkAPI.dispatch(getClientNotify({data: descriptionData, action}))
            const {order, worker, dependencies} = descriptionData;
            order && thunkAPI.dispatch(updateOrder(order));
            worker && thunkAPI.dispatch(updateUser(worker));
            dependencies && thunkAPI.dispatch(updateDeps(dependencies));
        }

        const {hasData: hasIsProfileData, fields: profData} = hasRequiredFields(orderData, isProfileFields);
        if (hasIsProfileData) {
            const isProfileData = await ordersAPI.edit({orderData: profData, changesType: 'isProfile'});
            const action = CLIENT_NOTS.EDIT_ORDER_IS_PROFILE;

            if (!isProfileData.isOkay) {
                errorData = isProfileData;
                errorAction = action;
                throw new Error('edit isProfile error');
            }
            thunkAPI.dispatch(getClientNotify({data: isProfileData, action}))
            const {order, worker} = isProfileData;
            order && thunkAPI.dispatch(updateOrder(order));
            worker && thunkAPI.dispatch(updateUser(worker));
        }

        const {hasData: hasWorkerData, fields: workData} = hasRequiredFields(orderData, workerFields);
        if (hasWorkerData) {
            const workerData = await ordersAPI.edit({orderData: workData, changesType: 'worker'});
            const action = CLIENT_NOTS.EDIT_ORDER_WORKER;
            if (!workerData.isOkay) {
                errorData = workerData;
                errorAction = action;
                throw new Error('edit worker error');
            }
            thunkAPI.dispatch(getClientNotify({data: workerData, action}))
            const {order, workers} = workerData;
            order && thunkAPI.dispatch(updateOrder(workerData.order));
            workers?.prev && thunkAPI.dispatch(updateUser(workers.prev));
            workers?.new && thunkAPI.dispatch(updateUser(workers.new));
        }

        const {hasData: hasCompletionData, fields: complData} = hasRequiredFields(orderData, completionDataFields);
        if (hasCompletionData) {
            const completionData = await ordersAPI.edit({orderData: complData, changesType: 'completionData'});
            const action = CLIENT_NOTS.EDIT_COMPLETION_DATA;
            if (!completionData.isOkay) {
                errorData = completionData;
                errorAction = action;
                throw new Error('edit completionData error');
            }
            thunkAPI.dispatch(getClientNotify({data: completionData, action}))
            const {order, worker, dependencies} = completionData;
            order && thunkAPI.dispatch(updateOrder(order));
            worker && thunkAPI.dispatch(updateUser(worker));
            dependencies && thunkAPI.dispatch(updateDeps(dependencies));
        }
        closeEditor();

    } catch(error) {
        console.log(error)
        thunkAPI.dispatch(getClientNotify({data: errorData, action: errorAction}));
    }
});

export const completeOrder = createAsyncThunk('orders/complete', async ({closeFn, ...rest}, thunkAPI) => {
    const data = await ordersAPI.complete(rest);
    thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.COMPLETE_ORDER}));
    if (data.isOkay) {
        closeFn();
        thunkAPI.dispatch(updateOrder(data.order));
        thunkAPI.dispatch(updateUser(data.worker));
    } else {
        console.log(data);
    }
});

export const getOrderEvents = createAsyncThunk('orders/events', async (orderId, thunkAPI) => {
    const thisOrder = (thunkAPI.getState()).orders.list.find(o => o._id ===orderId);
    
    const isLoading = thisOrder.details.events.loadState === 'loading';
    if (isLoading) return;

    thunkAPI.dispatch(setOrderEventLoading(orderId));
    const data = await ordersAPI.getEvents(orderId);
    if (data.isOkay) {
        thunkAPI.dispatch(setOrderEvents(data));
    } else {
        thunkAPI.dispatch(setOrderEvents({orderId, events: []}));
        thunkAPI.dispatch(getClientNotify({data, action: CLIENT_NOTS.ORDER_EVENTS}));
        console.log(data);
    }
});

const orderSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        setHandOver(state, {payload}) {
            state.list = state.list.map((o) => o._id === payload.orderId ?
            ({...o, details: {...o.details, isHandOver: payload.value}})
            : o)
        },
        changeMonth(state, {payload}) { //payload = str yyyy-mm
            const {month, multi} = payload;
            state.months = state.months.map(m => m.month === month ? ({...m, active: true}) : multi ? m : ({...m, active: false}))
        },
        unselectMonth(state, {payload}) {
            const {month, multi} = payload;
            state.months = state.months.map(m => !multi ? ({...m, active: false}) : m.month === month ? ({...m, active: false}) : m);
        },
        clearOrdersState(state) {
            state.error = null;
            state.isLoaded = false;
            state.isOrderCreating = false;
            state.list = [];
            state.months = null;
        },
        setLoaded(state, {payload}) {
            state.isLoaded = payload;
        },
        setMonthLoaded(state, {payload}) {
            if (payload.month) {  //Если передали конкретный месяц, который нужно загрузить
                state.months = state.months.map(m => m.month === payload.month ? ({...m, loaded: true}) : m)
                return;
            }
            state.months = state.months.map(m => m.active ? {   //По умолчанию подгружаем активный
                ...m,
                loaded: true,
            } : m);
        },
        setMonths(state, {payload}) {
            state.months = payload;
        },
        setOrders(state, {payload}) {
            state.list = [...state.list, ...payload.map((o) => ({...o, details: {...o.details, events: {loadState: 'notLoaded', list: []}}}))];
        },
        updateDeps(state, {payload}) {  //[order]
            state.list = state.list.map(o => {
                const thisOrder = payload.find((o2) => o2._id === o._id);
                if (thisOrder) {
                    return {
                        ...o,
                        details: {
                            ...o.details,
                            warranties: thisOrder.warranties,
                            repeats: thisOrder.repeats,
                        }
                    }
                }
                return o;
            });
        },
        setError(state, action) {
            state.error = action.payload;
        },
        insertNewOrder(state, {payload}) {
            state.list = [...state.list, payload];
        },
        updateOrder(state, {payload}) {
            state.list = state.list.map(order => order._id === payload._id ? payload : order);
        },
        updateOrders(state, {payload}) {
            state.list = state.list.map((order) => {
                // const updated = payload.find(newOrder => newOrder._id === order._id);
                // if (updated) {
                //     return updated;
                // }

                return order;
            });
        },
        dropOrder(state, {payload}) {
            state.list = state.list.filter(order => order._id !== payload);
        },
        setOrderEventLoading(state, {payload}) {
            state.list = state.list.map(o => o._id !== payload ? o : ({
                ...o,
                details: {
                    ...o.details,
                    events: {
                        loadState: 'loading',
                        list: []
                    }
                }
            }))
        },
        setOrderEvents(state, {payload}) {//@ts-ignore
            state.list = state.list.map(o => o._id !== payload.orderId ? o : ({
                ...o,
                details: {
                    ...o.details,
                    events: {
                        loadState: 'loaded',
                        list: payload.events,
                    }
                }
            }))
        },
        setNewOrderField(state, {payload}) {
            if (state.isOrderCreating !== payload) {
                state.isOrderCreating = payload;
            }
        },
        saveOrderLocal(state, {payload}) {
            state.isSavedOrderExists = true;
            localStorage.setItem('lastSavedOrder', JSON.stringify(payload));
        },
        updateTitleSize(state, {payload}) {
            state.orderTitles = {...state.orderTitles, ...payload}
            localStorage.setItem('titleSizes', JSON.stringify(state.orderTitles));
        },
        setSort(state, {payload}) {
            state.sort = payload;
            localStorage.setItem('orderSort', JSON.stringify(state.sort));
        },
        setSearch(state, {payload}) {
            state.search = payload;
            localStorage.setItem('orderSearch', JSON.stringify(state.search));
        },
        setFilters(state, {payload}) {
            state.filter = payload;
            localStorage.setItem('orderFilter', JSON.stringify(state.filter));
        },
        setDeps(state, {payload}) {
            state.deps = payload;
        },
        dropOrderImage(state, {payload}) {
            state.list = state.list.map(o => o._id !== payload.orderId ? o : (
                {
                    ...o,
                    BSOPhotos: o.BSOPhotos.filter((bsoName) => bsoName !== payload.fileName),
                    chequePhotos: o.chequePhotos.filter((chequeName) => chequeName !== payload.fileName),
                }
            ))
        },
        setSavedOrderExists(state, {payload}) {
            state.isSavedOrderExists = getLastSavedOrder(payload);
        }
    },
    extraReducers: (builder) => {
        
    },
});
export const { setSavedOrderExists, setOrderEventLoading, setOrderEvents, dropOrder, dropOrderImage, setDeps, setHandOver, changeMonth, updateTitleSize, unselectMonth, setSort, setSearch, setFilters, setMonthLoaded, clearOrdersState, updateDeps, setOrders, setMonths, setLoaded, setError, saveOrderLocal, insertNewOrder, updateOrder, setNewOrderField, updateOrders } = orderSlice.actions;

export default orderSlice.reducer;
